import React, { useEffect, useState, useCallback } from "react";
import { Card, Col, Row, Table, Spin, Empty, Tag, DatePicker, Tabs, Button, Modal, Input, Select, Switch, Popconfirm, message, Drawer, Divider, Tooltip, Typography, List } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, PlusOutlined, MinusOutlined, EditOutlined, DeleteOutlined, FilterOutlined, SaveOutlined, CloseCircleOutlined, BulbOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, CartesianGrid, Legend,
  BarChart,
  Bar,
} from "recharts";
import { useSelector } from "react-redux";
import HeaderCompo from "../../component/Header";
import { axiosInstance } from "../../utils/axiosInterceptor";
import { BE_HOST } from "../../config/config";
import SmsWorkflow from "../smsWorkflow/SmsWorkflow";
import axios from "axios";

// ── Formatters ────────────────────────────────────────────────────────────────
const fmtM = (v) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
};
const fmtPct   = (v) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(2)}%`);
const fmtNum   = (v) => (v == null || isNaN(v) ? "—" : Number(v).toLocaleString());
const monthLbl = (v) => {
  if (!v) return "";
  const [y, m] = v.split("-");
  return new Date(+y, +m - 1, 1).toLocaleString("default", { month: "short", year: "2-digit" });
};

// ── Change indicator ──────────────────────────────────────────────────────────
const Change = ({ pct, invertColor = false }) => {
  if (pct == null || isNaN(pct)) return <span className="text-gray-400">—</span>;
  const up   = pct >= 0;
  const good = invertColor ? !up : up;
  return (
    <span className={`font-semibold ${good ? "text-green-600" : "text-red-500"}`}>
      {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(pct).toFixed(1)}%
    </span>
  );
};

// ── GPR KPI Box ───────────────────────────────────────────────────────────────
const GprBox = ({ title, current, prev, qtd, invertColor = false, color = "#1f2a6d" }) => {
  const momPct = current != null && prev != null && prev !== 0
    ? parseFloat(((current - prev) / Math.abs(prev) * 100).toFixed(2))
    : null;
  return (
    <Card className="bg-white CardShadow cardAni h-full" styles={{ body: { padding: 16 } }}>
      <div className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">{title}</div>
      <div style={{ color }} className="text-3xl font-bold mb-1">{fmtM(current)}</div>
      <div className="text-xs text-gray-400 mb-2">Current Month</div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm text-gray-600">{fmtM(prev)}</span>
        <Change pct={momPct} invertColor={invertColor} />
      </div>
      <div className="text-xs text-gray-400 mb-3">vs Last Month</div>
      <div className="border-t pt-2">
        <div className="text-sm font-semibold text-gray-700">{fmtM(qtd)}</div>
        <div className="text-xs text-gray-400">QTD</div>
      </div>
    </Card>
  );
};

// ── Expandable sub-table for leaked NDC / Pharmacy ────────────────────────────
const ExpandedNdcRow = ({ drug, selectedMonth }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = `gcn=${encodeURIComponent(drug)}${selectedMonth ? `&month=${selectedMonth}` : ''}`;
    axiosInstance.get(`${BE_HOST}/api/management/leaked-ndc-detail?${params}`)
      .then((res) => setData(res?.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [drug, selectedMonth]);

  return (
    <Table
      dataSource={data}
      columns={[
        { title: "Pharmacy", dataIndex: "pharmacyName", key: "pharmacyName", ellipsis: true },
        { title: "State",    dataIndex: "state",         key: "state",         width: 60 },
        { title: "Leaked $", dataIndex: "leakedAmount",  key: "leakedAmount",  render: fmtM, width: 100 },
        { title: "Leaked Units", dataIndex: "leakedQty", key: "leakedQty",    render: fmtNum, width: 100 },
      ]}
      rowKey="accountNumber"
      size="small"
      pagination={false}
      loading={loading}
    />
  );
};

const ExpandedPharmacyRow = ({ accountNumber, selectedMonth }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = `accountNumber=${accountNumber}${selectedMonth ? `&month=${selectedMonth}` : ''}`;
    axiosInstance.get(`${BE_HOST}/api/management/leaked-pharmacy-detail?${params}`)
      .then((res) => setData(res?.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [accountNumber, selectedMonth]);

  return (
    <Table
      dataSource={data}
      columns={[
        { title: "Drug (GCN)", dataIndex: "drug",         key: "drug",         ellipsis: true },
        { title: "Leaked $",   dataIndex: "leakedAmount", key: "leakedAmount", render: fmtM, width: 100 },
        { title: "Leaked Units", dataIndex: "leakedQty",  key: "leakedQty",   render: fmtNum, width: 100 },
      ]}
      rowKey="drug"
      size="small"
      pagination={false}
      loading={loading}
    />
  );
};

// ── Build query string from month + filters ───────────────────────────────────
const buildParams = (month, filters) => {
  const parts = [];
  if (month) parts.push(`month=${month}`);
  if (filters?.states?.length)       parts.push(`states=${filters.states.join(',')}`);
  if (filters?.accountTypes?.length) parts.push(`accountTypes=${filters.accountTypes.join(',')}`);
  if (filters?.rebateTypes?.length)  parts.push(`rebateTypes=${filters.rebateTypes.join(',')}`);
  if (filters?.groupId)              parts.push(`groupId=${filters.groupId}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

// ── Main Dashboard Content ────────────────────────────────────────────────────
const DashboardContent = ({ selectedMonth, filters , data, isLoading }) => {
  const [loadingMem, setLoadingMem] = useState(true);
  const [loadingGpr, setLoadingGpr] = useState(true);
  const [membership, setMembership] = useState(null);
  const [gpr,        setGpr]        = useState(null);
  const [gprError,   setGprError]   = useState(false);
  const [memError,   setMemError]   = useState(false);

  const fetchData = useCallback(() => {
    const qs = buildParams(selectedMonth, filters);

    setLoadingMem(true);
    setMemError(false);
    axiosInstance.get(`${BE_HOST}/api/management/membership-summary${qs}`, { timeout: 60000 })
      .then((res) => setMembership(res?.data || null))
      .catch((err) => { console.error("[membership]", err); setMemError(true); })
      .finally(() => setLoadingMem(false));

    setLoadingGpr(true);
    setGprError(false);
    axiosInstance.get(`${BE_HOST}/api/management/gpr-detail${qs}`, { timeout: 60000 })
      .then((res) => setGpr(res?.data || null))
      .catch((err) => { console.error("[gpr]", err); setGprError(true); })
      .finally(() => setLoadingGpr(false));
  }, [selectedMonth, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cur   = membership?.current       || {};
  const prev  = membership?.prev          || {};
  const prev2 = membership?.months?.[2]   || null;
  const yoy   = membership?.yoy           || null;
  const qtd   = membership?.qtd           || {};

  const pctChange = (a, b) => (!b || b === 0) ? null : parseFloat(((a - b) / Math.abs(b) * 100).toFixed(2));
  const yoyKeys = ['Total_Rx','Generic_Rx','Brand_Rx','OnStop_Generic','RTL2_Rx','Specialty','HIV_Sum',
    'NDC_Exclusions','WAC_Dollar_Impact','GCR_Percentage','SGCR_Percentage','GPR_Percentage'];
  const yoyChange = {};
  yoyKeys.forEach(k => { yoyChange[k] = pctChange(cur[k], yoy?.[k]); });

  const memRows = [
    { key: "current", label: `Current (${monthLbl(cur.month)})`,                                         data: cur,       compare: prev  },
    { key: "prev",    label: `Prev (${monthLbl(prev.month)})`,                                           data: prev,      compare: prev2 },
    { key: "prev2",   label: `2 Mo Ago (${monthLbl(prev2?.month)})`,                                     data: prev2,     compare: null  },
    { key: "yoy",     label: `YoY Change (${monthLbl(cur.month)} vs ${yoy?.month ? monthLbl(yoy.month) : "N/A"})`, data: yoyChange, isChange: true },
    { key: "qtd",     label: `QTD (${membership?.currentQuarter || ""})`,                                data: qtd,       compare: null  },
  ];

  const cell = (r, key, fmt = fmtNum) => {
    if (r.isChange) {
      const pct = r.data?.[key];
      if (pct == null) return <span className="text-gray-400">—</span>;
      const neutral = Math.abs(pct) < 0.01;
      const up = !neutral && pct > 0;
      const clr = neutral ? "text-gray-400" : up ? "text-green-600" : "text-red-500";
      return <span className={`font-semibold ${clr}`}>{pct >= 0 ? "+" : ""}{pct}%</span>;
    }

    const val = r.data?.[key];
    const cmp = r.compare?.[key];

    if (cmp == null) return fmt(val);

    const diff     = (val != null && cmp != null) ? val - cmp : null;
    const pctChg   = (diff != null && Math.abs(cmp) > 0)
      ? parseFloat(((diff / Math.abs(cmp)) * 100).toFixed(1)) : null;
    const neutral  = diff != null && Math.abs(diff) < 0.001;
    const up       = !neutral && diff != null && diff > 0;

    const icon = diff == null ? null
      : neutral ? <span style={{ fontSize: 10 }} className="text-gray-400">—</span>
      : up      ? <ArrowUpOutlined   style={{ fontSize: 10 }} className="text-green-500" />
      :           <ArrowDownOutlined style={{ fontSize: 10 }} className="text-red-500"  />;

    const tip = (
      <div style={{ lineHeight: 1.8 }}>
        <div>Current: {fmt(val)}</div>
        <div>Previous: {fmt(cmp)}</div>
        {pctChg != null && <div>Change: {pctChg >= 0 ? "+" : ""}{pctChg}%</div>}
      </div>
    );

    return (
      <Tooltip title={tip}>
        <span className="cursor-help inline-flex items-center gap-1">{fmt(val)}{icon}</span>
      </Tooltip>
    );
  };

  const memColumns = [
    { title: "Period",      dataIndex: "label", key: "label", width: 210,
      render: (v, r) => <span className={`font-semibold ${r.key === "qtd" ? "text-blue-700" : "text-gray-700"}`}>{v}</span> },
    { title: "GCR %",      key: "gcr",   width: 90,  render: (_, r) => cell(r, "GCR_Percentage",  fmtPct) },
    { title: "sGCR %",     key: "sgcr",  width: 90,  render: (_, r) => cell(r, "SGCR_Percentage", fmtPct) },
    { title: "GPR %",      key: "gpr",   width: 90,  render: (_, r) => cell(r, "GPR_Percentage",  fmtPct) },
    { title: "Total RX",   key: "net",   width: 110, render: (_, r) => cell(r, "Total_Rx")                },
    { title: "Generic RX", key: "gen",   width: 110, render: (_, r) => cell(r, "Generic_Rx")              },
    { title: "Onestop",    key: "one",   width: 100, render: (_, r) => cell(r, "OnStop_Generic")          },
    { title: "RTL2",       key: "rtl2",  width: 100, render: (_, r) => cell(r, "RTL2_Rx")                 },
    { title: "Brand RX",   key: "brand", width: 110, render: (_, r) => cell(r, "Brand_Rx")                },
    { title: "GLP1 Units", key: "glp1u", width: 130, render: (_, r) => cell(r, "NDC_Exclusions")          },
    { title: "GLP1 $ Vol", key: "glp1d", width: 130, render: (_, r) => cell(r, "WAC_Dollar_Impact", fmtM) },
  ];

  const gprCur  = gpr?.current || {};
  const gprPrev = gpr?.prev    || {};
  const gprQtd  = gpr?.qtd     || {};
  const trend   = gpr?.trend   || [];

  const ndcColumns = [
    { title: "Drug (GCN)",    dataIndex: "drug",          key: "drug",          ellipsis: true, width:160 },
    { title: "Leaked $",      dataIndex: "leakedAmount",  key: "leakedAmount",  render: fmtM,   width: 110, align:"right", sorter:true,
      sorter:true, sorter: (a, b) =>
            parseFloat(a?.leakedAmount || 0) -
            parseFloat(b?.leakedAmount || 0)
     },
    { title: "Leaked Units",  dataIndex: "leakedQty",     key: "leakedQty",     render: fmtNum, align:"right", sorter:true, width:110, sorter:true, 
      sorter: (a, b) =>
            parseFloat(a?.leakedQty || 0) -
            parseFloat(b?.leakedQty || 0),},
    { title: "# Pharmacies",  dataIndex: "pharmacyCount", key: "pharmacyCount",  align:"right" , width:120, sorter:true, sorter: (a, b) =>
            parseFloat(a?.pharmacyCount || 0) -
            parseFloat(b?.pharmacyCount || 0),},
  ];

  const pharmColumns = [
    { title: "Pharmacy",  dataIndex: "pharmacyName", key: "pharmacyName", ellipsis: true },
    { title: "State",     dataIndex: "state",         key: "state",         width: 65 },
    { title: "Leaked $",  dataIndex: "leakedAmount",  key: "leakedAmount",  render: fmtM,   width: 110, align:"right",sorter: (a, b) =>
            parseFloat(a?.leakedAmount || 0) -
            parseFloat(b?.leakedAmount || 0), },
    { title: "GPR %",     dataIndex: "gprPct",        key: "gprPct",        render: fmtPct, width: 80 , align:"right", sorter: (a, b) =>
            parseFloat(a?.gprPct || 0) -
            parseFloat(b?.gprPct || 0),},
  ];

  return (
    <>
    <Spin spinning={isLoading}>
  <Card
    bordered={false}
    className="rounded-2xl shadow-xl overflow-hidden bg-white mb-4"
    bodyStyle={{ padding: 24 }}
  >
    <div className="mb-6 flex items-center gap-3">
      <span className="rounded-full bg-blue-600 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-white">
         Insights
      </span>
    </div>

    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#000437] via-[#000437] to-[#2958E2] p-8 text-white">

      <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-white/10"></div>
      <div className="absolute bottom-0 right-24 h-24 w-24 rounded-full bg-white/10"></div>

      <div className="relative z-10">

        <div className="flex items-center gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-blue-100">
              AI Summary
            </p>
          </div>
        </div>

        <p className="mt-5 max-w-7xl leading-8 text-blue-50">
          {data?.summary}
        </p>

        <div className="mt-6 flex flex-wrap gap-3">
        </div>
      </div>
    </div>

    <div className="mt-8 grid grid-cols-12 gap-6">

      <div className="col-span-12 lg:col-span-8 bg-[#f4f2fd] p-6 rounded-md">

        <div className="mb-4 flex items-center gap-2">
          <h3 className="font-semibold">
            Key Highlights
          </h3>

          <span className="flex rounded-full w-7 h-7 bg-blue-100  flex-shrink-0 items-center justify-center text-xs text-blue-700">
            {data?.highlights?.length || 0}
          </span>
        </div>

        <div className="space-y-4">

          {data?.highlights?.map((item, index) => {

            const colors = [
              "border-green-400",
              "border-blue-400",
              "border-orange-400",
            ];

            return (
              <div
                key={index}
                className={`rounded-2xl border-l-4 ${colors[index % 3]} border border-gray-200 bg-white p-5 shadow-sm transition hover:shadow-lg`}
              >
                <div className="flex justify-between">

                  <div className="flex gap-4 items-center">

                    <div className="flex h-2 w-2 flex-shrink-0 items-center justify-center rounded-full bg-gray-900 font-semibold">
                    </div>

                    <div>

                      <p className="text-gray-700 leading-2">
                        {item}
                      </p>

                    </div>

                  </div>
                </div>
              </div>
            );
          })}

        </div>

      </div>

      <div className="col-span-12 lg:col-span-4 bg-purple-100 p-6 rounded-md">

        <div className="mb-4 flex items-center gap-2">

          <h3 className="font-semibold">
            Recommended Actions
          </h3>

          <span className="rounded-full flex items-center justify-center flex-shrink-0 w-7 h-7 bg-purple-200 text-xs text-purple-700">
            {data?.actions?.length || 0}
          </span>

        </div>

        <div className="space-y-4">

          {data?.actions?.map((item, index) => (
            <div
              key={index}
              className="rounded-2xl border-l-4 border-purple-500 border bg-white p-5 shadow-sm hover:shadow-lg"
            >
              <div className="mb-3 text-xs font-bold uppercase text-purple-600">
                Action {index + 1}
              </div>

              <p className="leading-7 text-gray-700">
                {item}
              </p>
            </div>
          ))}

        </div>

      </div>

    </div>

  </Card>
</Spin>
      {/* ── Section A: Membership Performance ────────────────────────── */}
      <Spin spinning={loadingMem}>
        <Card
          title={<span className="font-bold text-gray-700">Membership Performance</span>}
          className="bg-white CardShadow cardAni mb-5"
          extra={memError && !loadingMem && (
            <span className="text-sm text-red-500">
              Failed to load.{" "}
              <button className="underline text-blue-600 cursor-pointer" onClick={fetchData}>Retry</button>
            </span>
          )}
        >
          {!loadingMem && !membership?.current ? (
            <Empty description="No data available" />
          ) : !loadingMem ? (
            <Table
              dataSource={memRows}
              columns={memColumns}
              rowKey="key"
              size="small"
              pagination={false}
              scroll={{ x: 900 }}
              rowClassName={(r) => r.key === "qtd" ? "bg-gray-50" : r.key === "yoy" ? "bg-blue-50" : ""}
            />
          ) : null}
        </Card>
      </Spin>

      {/* ── GPR Section ─────────────────────────────────────────────── */}
      <Spin spinning={loadingGpr}>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-lg font-bold text-white bg-blue-900 px-4 py-1 rounded">GPR</span>
          {gpr?.currentQuarter && (
            <span className="text-sm text-gray-500">{gpr.currentQuarter}</span>
          )}
          {gprError && !loadingGpr && (
            <span className="text-sm text-red-500">
              Failed to load.{" "}
              <button className="underline text-blue-600 cursor-pointer" onClick={fetchData}>Retry</button>
            </span>
          )}
        </div>

        {/* Boxes C / D / E */}
        <Row gutter={[16, 16]} className="mb-5">
          <Col xs={24} md={8}>
            <GprBox title="Purchase Amount (from AAPA)" current={gprCur.purchaseAmount} prev={gprPrev.purchaseAmount} qtd={gprQtd.purchaseAmount} color="#1f2a6d" />
          </Col>
          <Col xs={24} md={8}>
            <GprBox title="Dispense Amount (Total Market)" current={gprCur.dispenseAmount} prev={gprPrev.dispenseAmount} qtd={gprQtd.dispenseAmount} color="#2e7d32" />
          </Col>
          <Col xs={24} md={8}>
            <GprBox title="Leaked Amount (Dispense − Purchase)" current={gprCur.leakedAmount} prev={gprPrev.leakedAmount} qtd={gprQtd.leakedAmount} invertColor color="#c62828" />
          </Col>
        </Row>

        {/* GPR Trend Chart */}
        <Row gutter={[16, 16]} className="mb-5">
          <Col xs={24}>
            <Card title="GPR % Change by Month" className="bg-white CardShadow cardAni">
              {!trend.length ? <Empty description="No data available" /> : (
                // <ResponsiveContainer width="100%" height={280}>
                //   <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                //     <CartesianGrid strokeDasharray="3 3" vertical={false} />
                //     <XAxis dataKey="month" tickFormatter={monthLbl} />
                //     <YAxis tickFormatter={(v) => `${v}%`} domain={["auto", "auto"]} />
                //     <RechartsTooltip formatter={(v) => `${v}%`} labelFormatter={monthLbl} />
                //     <Legend />
                //     <Line type="monotone" dataKey="gprPctDollars" name="GPR % ($)" stroke="#1f2a6d" strokeWidth={2.5} dot={{ r: 4 }} />
                //   </LineChart>
                // </ResponsiveContainer>
                <div
                  style={{
                    width: "100%",
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <div
                    style={{
                      minWidth: `${Math.max(trend.length * 80, 500)}px`,
                      height: 280,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={trend}
                        margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                        barCategoryGap="20%"
                      >
                        <defs>
                              {/* <pattern
                                id="patternLabel"
                                patternUnits="userSpaceOnUse"
                                width="10"
                                height="10"
                              >
                                <rect width="10" height="10" fill="#0B9CE9" />
                                <path
                                  d="M0,10 l10,-10 M-2,2 l4,-4 M8,12 l4,-4"
                                  stroke="rgba(255,255,255,0.2)"
                                  strokeWidth="4"
                                />
                              </pattern> */}
                              <pattern
                                id="patternLabel"
                                patternUnits="userSpaceOnUse"
                                width="10"
                                height="10"
                              >
                                <rect width="10" height="10" fill="#001997" />
                                <path
                                  d="M0,10 l10,-10"
                                  stroke="rgba(255,255,255,0.2)"
                                  strokeWidth="4"
                                />
                              </pattern>
                            </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  
                        <XAxis
                          dataKey="month"
                          tickFormatter={monthLbl}
                        />

                        <YAxis
                          tickFormatter={(value) => `${value}%`}
                        />

                        <RechartsTooltip
                          cursor={{ fill: "rgba(0,0,0,0.05)" }}
                          labelFormatter={(label) => monthLbl(label)}
                          formatter={(value) => [`${value}%`, "GPR % ($)"]}
                        />

                        <Legend />
                  
                        <Bar
                          dataKey="gprPctDollars"
                          name="GPR % ($)"
                          fill="url(#patternLabel)"
                          radius={[6, 6, 0, 0]}
                          barSize={30}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Top Leaked Tables — Expandable, Top 20, Scrollable */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card title="Top 20 Leaked NDCs" className="bg-white CardShadow cardAni">
              {!gpr?.topNdc?.length ? <Empty description="No data available" /> : (
                <Table
                  dataSource={gpr.topNdc}
                  columns={ndcColumns}
                  rowKey="drug"
                  size="small"
                  pagination={false}
                  scroll={{ y: 400 }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <ExpandedNdcRow drug={record.drug} selectedMonth={selectedMonth} />
                    ),
                    expandIcon: ({ expanded, onExpand, record }) => (
                      <span onClick={(e) => onExpand(record, e)} className="cursor-pointer text-blue-600">
                        {expanded ? <MinusOutlined /> : <PlusOutlined />}
                      </span>
                    ),
                  }}
                />
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Top 20 Leaked Pharmacies" className="bg-white CardShadow cardAni">
              {!gpr?.topPharmacies?.length ? <Empty description="No data available" /> : (
                <Table
                  dataSource={gpr.topPharmacies}
                  columns={pharmColumns}
                  rowKey="accountNumber"
                  size="small"
                  pagination={false}
                  scroll={{ y: 400 }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <ExpandedPharmacyRow accountNumber={record.accountNumber} selectedMonth={selectedMonth} />
                    ),
                    expandIcon: ({ expanded, onExpand, record }) => (
                      <span onClick={(e) => onExpand(record, e)} className="cursor-pointer text-blue-600">
                        {expanded ? <MinusOutlined /> : <PlusOutlined />}
                      </span>
                    ),
                  }}
                />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </>
  );
};

// ── Account Groups Tab ────────────────────────────────────────────────────────
const AccountGroups = ({fetchFilterOpts}) => {
  const [groups, setGroups]             = useState([]);
  const [pharmacies, setPharmacies]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [modalOpen, setModalOpen]       = useState(false);
  const [editGroup, setEditGroup]       = useState(null);
  const [formName, setFormName]         = useState("");
  const [formAccounts, setFormAccounts] = useState([]);
  const [saving, setSaving]             = useState(false);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${BE_HOST}/api/management/groups`);
      setGroups(res?.data?.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchPharmacies = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${BE_HOST}/api/management/available-pharmacies`);
      setPharmacies(res?.data?.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchGroups(); fetchPharmacies(); }, [fetchGroups, fetchPharmacies]);

  const openCreate = () => { setEditGroup(null); setFormName(""); setFormAccounts([]); setModalOpen(true); };
  const openEdit = (g) => {
    setEditGroup(g);
    setFormName(g.group_name);
    setFormAccounts(JSON.parse(g.account_ids || '[]'));
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formAccounts.length) { message.warning("Enter a group name and select pharmacies"); return; }
    setSaving(true);
    try {
      if (editGroup) {
        await axiosInstance.put(`${BE_HOST}/api/management/groups/${editGroup.id}`, { group_name: formName, account_ids: formAccounts });
        message.success("Group updated");
      } else {
        await axiosInstance.post(`${BE_HOST}/api/management/groups`, { group_name: formName, account_ids: formAccounts });
        message.success("Group created");
      }
      setModalOpen(false);
      fetchGroups();
      fetchFilterOpts()
    } catch (err) { message.error("Failed to save group"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`${BE_HOST}/api/management/groups/${id}`);
      message.success("Group deleted");
      fetchGroups();
    } catch (err) { message.error("Failed to delete"); }
  };

  const handleToggle = async (g) => {
    try {
      await axiosInstance.put(`${BE_HOST}/api/management/groups/${g.id}`, { active: !g.active });
      fetchGroups();
    } catch (err) { message.error("Failed to update"); }
  };

  const columns = [
    { title: "Group Name", dataIndex: "group_name", key: "group_name",
      render: (v) => <span className="font-semibold">{v}</span> },
    { title: "Pharmacies", dataIndex: "accountCount", key: "accountCount", width: 100 },
    { title: "Active", dataIndex: "active", key: "active", width: 80,
      render: (v, r) => <Switch size="small" checked={v} onChange={() => handleToggle(r)} /> },
    { title: "Actions", key: "actions", width: 120,
      render: (_, r) => (
        <div className="flex gap-2">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          <Popconfirm title="Delete this group?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <span className="text-gray-600">{groups.length} group(s)</span>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Create Group</Button>
      </div>

      <Spin spinning={loading}>
        {!groups.length && !loading ? (
          <Card className="CardShadow"><Empty description="No groups created yet. Click 'Create Group' to start." /></Card>
        ) : (
          <Table
            dataSource={groups}
            columns={columns}
            rowKey="id"
            size="middle"
            pagination={false}
            expandable={{
              expandedRowRender: (record) => (
                <div className="flex flex-wrap gap-2 py-2">
                  {record.pharmacies?.map((p) => (
                    <Tag key={p.acct_id} color="blue">{p.name} ({p.state}) — {p.acct_id}</Tag>
                  ))}
                </div>
              ),
              expandIcon: ({ expanded, onExpand, record }) => (
                <span onClick={(e) => onExpand(record, e)} className="cursor-pointer text-blue-600">
                  {expanded ? <MinusOutlined /> : <PlusOutlined />}
                </span>
              ),
            }}
          />
        )}
      </Spin>

      <Modal
        title={editGroup ? "Edit Group" : "Create Group"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        width={600}
        destroyOnHidden
      >
        <div className="mb-3">
          <label className="text-sm font-medium text-gray-600 block mb-1">Group Name</label>
          <Input
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="e.g., New York Pharmacies"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-600 block mb-1">
            Select Pharmacies ({formAccounts.length} selected)
          </label>
          <Select
            mode="multiple"
            showSearch
            placeholder="Search and select pharmacies..."
            style={{ width: "100%" }}
            value={formAccounts}
            onChange={setFormAccounts}
            optionFilterProp="label"
            maxTagCount={5}
            options={pharmacies.map((p) => ({
              value: p.acct_id,
              label: `${p.name} (${p.state || ''}) — ${p.acct_id}`,
            }))}
          />
        </div>
      </Modal>
    </>
  );
};

// ── Main Component with Tabs ──────────────────────────────────────────────────
const PowerBiSales = () => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const userAuth = useSelector((state) => state.userAuth.decodedToken?.user);
  

  // Filters
  const [filters, setFilters]           = useState({});
  const [filterDrawer, setFilterDrawer] = useState(false);
  const [filterOpts, setFilterOpts]     = useState({ states: [], accountTypes: [], rebateTypes: [], groups: [] });
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveFilterName, setSaveFilterName] = useState("");

  // Draft filter state (applied on "Apply")
  const [draftStates, setDraftStates]           = useState([]);
  const [draftAccountTypes, setDraftAccountTypes] = useState([]);
  const [draftRebateTypes, setDraftRebateTypes] = useState([]);
  const [draftGroupId, setDraftGroupId]         = useState(null);
  const [isInsightCalling,setIsInsightCalling] = useState(false)
  const [aiInSightsData, setAiInSightsData] = useState({
    summary: "",
    highlights: [],
    actions: [],
  });
  const currentDate = new Date().toISOString().split("T")[0]

  const activeFilterCount = (filters.states?.length || 0) + (filters.accountTypes?.length || 0) + (filters.rebateTypes?.length || 0) + (filters.groupId ? 1 : 0);
  const fetchFilterOpts = useCallback(async () => {
    try {
      const [optsRes, savedRes] = await Promise.all([
        axiosInstance.get(`${BE_HOST}/api/management/filter-options`),
        axiosInstance.get(`${BE_HOST}/api/management/saved-filters`),
      ]);
      setFilterOpts(optsRes?.data || {});
      setSavedFilters(savedRes?.data?.data || []);
    } catch (err) { console.error(err); }
  }, []);


  const getAiInsights = async() => {
    setIsInsightCalling(true)
    try {
      const response = await axios.get(`${BE_HOST}/ai_management/insights?user_id=${userAuth?.id}&date=${currentDate}`,
        {
          headers: {
            Authorization: "Basic aapa:Aapa@123",
          },
        }
      )
      setAiInSightsData(response?.data?.data?.ai_summary);
    } catch (error) {
      console.log(error,"Error")
    }finally{
      setIsInsightCalling(false)
    }
  }

  useEffect(() => { fetchFilterOpts(); }, [fetchFilterOpts]);
  useEffect(()=>{
    getAiInsights()
  },[])

  const openFilterDrawer = () => {
    setDraftStates(filters.states || []);
    setDraftAccountTypes(filters.accountTypes || []);
    setDraftRebateTypes(filters.rebateTypes || []);
    setDraftGroupId(filters.groupId || null);
    setFilterDrawer(true);
  };

  const applyFilters = () => {
    setFilters({
      states: draftStates.length ? draftStates : undefined,
      accountTypes: draftAccountTypes.length ? draftAccountTypes : undefined,
      rebateTypes: draftRebateTypes.length ? draftRebateTypes : undefined,
      groupId: draftGroupId || undefined,
    });
    setFilterDrawer(false);
  };

  const clearFilters = () => {
    setDraftStates([]); setDraftAccountTypes([]); setDraftRebateTypes([]); setDraftGroupId(null);
    setFilters({});
    setFilterDrawer(false);
  };

  const loadSavedFilter = (config) => {
    setDraftStates(config.states || []);
    setDraftAccountTypes(config.accountTypes || []);
    setDraftRebateTypes(config.rebateTypes || []);
    setDraftGroupId(config.groupId || null);
  };

  const saveCurrentFilter = async () => {
    if (!saveFilterName.trim()) { message.warning("Enter a filter name"); return; }
    try {
      await axiosInstance.post(`${BE_HOST}/api/management/saved-filters`, {
        filter_name: saveFilterName,
        filter_config: { states: draftStates, accountTypes: draftAccountTypes, rebateTypes: draftRebateTypes, groupId: draftGroupId },
      });
      message.success("Filter saved");
      setSaveFilterName("");
      fetchFilterOpts();
    } catch (err) { message.error("Failed to save filter"); }
  };

  const deleteSavedFilter = async (id) => {
    try {
      await axiosInstance.delete(`${BE_HOST}/api/management/saved-filters/${id}`);
      fetchFilterOpts();
    } catch (err) { message.error("Failed to delete"); }
  };

  return (
    <>
      <HeaderCompo initialColor="#000833" />
      <div className="px-4 py-5 min-h-screen bg-gray-50">

        {/* Page header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">Management Dashboard</h2>
          <div className="flex items-center gap-3">
            {activeTab === "dashboard" && (
              <>
                <Button
                  icon={<FilterOutlined />}
                  onClick={openFilterDrawer}
                  type={activeFilterCount ? "primary" : "default"}
                >
                  Filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
                </Button>
                <DatePicker
                  picker="month"
                  format="MMM YYYY"
                  placeholder="Select Month"
                  value={selectedMonth ? dayjs(selectedMonth, "YYYY-MM") : null}
                  onChange={(date) => setSelectedMonth(date ? date.format("YYYY-MM") : null)}
                  allowClear
                  style={{ width: 150 }}
                />
              </>
            )}
          </div>
        </div>

        {/* Active filter tags */}
        {activeFilterCount > 0 && activeTab === "dashboard" && (
          <div className="flex flex-wrap gap-2 mb-3">
            {filters.states?.map(s => <Tag key={s} closable onClose={() => setFilters(f => ({ ...f, states: f.states.filter(x=>x!==s) }))} color="blue">{s}</Tag>)}
            {filters.accountTypes?.map(s => <Tag key={s} closable onClose={() => setFilters(f => ({ ...f, accountTypes: f.accountTypes.filter(x=>x!==s) }))} color="green">{s}</Tag>)}
            {filters.rebateTypes?.map(s => <Tag key={s} closable onClose={() => setFilters(f => ({ ...f, rebateTypes: f.rebateTypes.filter(x=>x!==s) }))} color="orange">{s}</Tag>)}
            {filters.groupId && <Tag closable onClose={() => setFilters(f => ({ ...f, groupId: undefined }))} color="purple">Group: {filterOpts.groups?.find(g=>g.id===filters.groupId)?.group_name || filters.groupId}</Tag>}
            <Tag className="cursor-pointer" color="red" onClick={clearFilters}><CloseCircleOutlined /> Clear All</Tag>
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "dashboard",
              label: "Dashboard",
              children: <DashboardContent selectedMonth={selectedMonth} filters={filters} isLoading={isInsightCalling} data={aiInSightsData}/>,
            },
            {
              key: "trend-reversal",
              label: "Trend Reversal",
              children: <SmsWorkflow embedded />,
            },
            {
              key: "account-groups",
              label: "Account Groups",
              children: <AccountGroups fetchFilterOpts = {fetchFilterOpts} />,
            },

          ]}
        />
      </div>

      {/* Filter Drawer */}
      <Drawer
        title="Dashboard Filters"
        open={filterDrawer}
        onClose={() => setFilterDrawer(false)}
        width={400}
        footer={
          <div className="flex justify-between">
            <Button onClick={clearFilters}>Clear All</Button>
            <Button type="primary" onClick={applyFilters}>Apply Filters</Button>
          </div>
        }
      >
        <div className="mb-4">
          <label className="text-sm font-medium text-gray-600 block mb-1">State</label>
          <Select mode="multiple" placeholder="All states" style={{ width: "100%" }} value={draftStates} onChange={setDraftStates}
            options={filterOpts.states?.map(s => ({ value: s, label: s }))} allowClear showSearch />
        </div>

        <div className="mb-4">
          <label className="text-sm font-medium text-gray-600 block mb-1">Account Type</label>
          <Select mode="multiple" placeholder="All types" style={{ width: "100%" }} value={draftAccountTypes} onChange={setDraftAccountTypes}
            options={filterOpts.accountTypes?.map(s => ({ value: s, label: s }))} allowClear />
        </div>

        <div className="mb-4">
          <label className="text-sm font-medium text-gray-600 block mb-1">Rebate Type</label>
          <Select mode="multiple" placeholder="All" style={{ width: "100%" }} value={draftRebateTypes} onChange={setDraftRebateTypes}
            options={filterOpts.rebateTypes?.map(s => ({ value: s, label: s }))} allowClear />
        </div>

        <div className="mb-4">
          <label className="text-sm font-medium text-gray-600 block mb-1">Pharmacy Group</label>
          <Select placeholder="No group filter" style={{ width: "100%" }} value={draftGroupId} onChange={setDraftGroupId}
            options={filterOpts.groups?.map(g => ({ value: g.id, label: g.group_name }))} allowClear />
        </div>

        <Divider />

        {/* Save current filter */}
        <div className="mb-4">
          <label className="text-sm font-medium text-gray-600 block mb-1">Save This Filter</label>
          <div className="flex gap-2">
            <Input placeholder="Filter name..." value={saveFilterName} onChange={(e) => setSaveFilterName(e.target.value)} />
            <Button icon={<SaveOutlined />} onClick={saveCurrentFilter}>Save</Button>
          </div>
        </div>

        {/* Load saved filters */}
        {savedFilters.length > 0 && (
          <>
            <label className="text-sm font-medium text-gray-600 block mb-2">Saved Filters</label>
            {savedFilters.map((sf) => (
              <div key={sf.id} className="flex justify-between items-center mb-2 p-2 bg-gray-50 rounded">
                <span className="cursor-pointer text-blue-600 hover:underline" onClick={() => loadSavedFilter(sf.filter_config)}>{sf.filter_name}</span>
                <Popconfirm title="Delete?" onConfirm={() => deleteSavedFilter(sf.id)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            ))}
          </>
        )}
      </Drawer>
    </>
  );
};

export default PowerBiSales;
